import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, watch } from 'vue';
import type { RowDoubleClickedEvent } from 'ag-grid-community';
import { getLSDefaults } from '@/utils';
import Entitlements from '@/Entitlements';
import { useUser } from '@/useUser';
import { useToken } from '@/useToken';
import { useVerificationToken } from '@/useVerificationToken';

interface ILSReportProps {
  domainId: number;
  apiUrl?: string;
  apiRoot?: string;
  authToken?: string;
  autoLoad?: boolean;
  className?: string;
  onFullScreen?: (fullScreen: boolean) => void;
  useSummaryMode?: boolean;
  filters?: boolean;
  groups?: boolean;
  views?: boolean;
  strats?: boolean;
  validations?: boolean;
  partitions?: boolean;
  sets?: boolean;
  asOfDate?: boolean;
  exportToExcel?: boolean;
  cutCopyPaste?: boolean;
  expand?: boolean;
  summaryMode?: boolean;
  controlBar?: boolean;
  dataAlternates?: boolean;
  subdomainBox?: boolean;
  isParentDomainPk?: boolean;
  onSelectionChanged?: (sel: string[]) => void;
  onRowDoubleClicked?: (sel: RowDoubleClickedEvent) => void;
  canUserEditReport?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'LSReport',
  props: {
    domainId: { type: Number, required: true },
    apiUrl: { type: String, required: false },
    apiRoot: { type: String, required: false },
    authToken: { type: String, required: false },
    autoLoad: { type: Boolean, required: false, default: false },
    className: { type: String, required: false },
    onFullScreen: { type: Function, required: false },
    useSummaryMode: { type: Boolean, required: false, default: false },
    filters: { type: Boolean, required: false, default: false },
    groups: { type: Boolean, required: false, default: false },
    views: { type: Boolean, required: false, default: false },
    strats: { type: Boolean, required: false, default: false },
    validations: { type: Boolean, required: false, default: false },
    partitions: { type: Boolean, required: false, default: false },
    sets: { type: Boolean, required: false, default: false },
    asOfDate: { type: Boolean, required: false, default: false },
    exportToExcel: { type: Boolean, required: false, default: false },
    cutCopyPaste: { type: Boolean, required: false, default: false },
    expand: { type: Boolean, required: false, default: false },
    summaryMode: { type: Boolean, required: false, default: false },
    controlBar: { type: Boolean, required: false, default: false },
    dataAlternates: { type: Boolean, required: false, default: false },
    subdomainBox: { type: Boolean, required: false, default: false },
    isParentDomainPk: { type: Boolean, required: false, default: false },
    onSelectionChanged: { type: Function, required: false },
    onRowDoubleClicked: { type: Function, required: false },
    canUserEditReport: { type: Boolean, required: false, default: false }
  },
  setup(__props: any) {

const props = __props as { domainId: number, apiUrl?: string, apiRoot?: string, authToken?: string, autoLoad: boolean, className?: string, onFullScreen?: (fullScreen: boolean) => void, useSummaryMode: boolean, filters: boolean, groups: boolean, views: boolean, strats: boolean, validations: boolean, partitions: boolean, sets: boolean, asOfDate: boolean, exportToExcel: boolean, cutCopyPaste: boolean, expand: boolean, summaryMode: boolean, controlBar: boolean, dataAlternates: boolean, subdomainBox: boolean, isParentDomainPk: boolean, onSelectionChanged?: (sel: string[]) => void, onRowDoubleClicked?: (sel: RowDoubleClickedEvent) => void, canUserEditReport: boolean };



const { user } = useUser();
const { token } = useToken();
const { verificationToken } = useVerificationToken();

const loaded = ref<boolean>(false);
const defaultView = ref<string | undefined>(undefined);
const canEdit = ref<boolean>(props.canUserEditReport);

const apiRoot = computed<string>(() => props.apiRoot ?? window.__WEBAPI_URL ?? '/api/');
const jwt = computed<string | null>(() => props.authToken ?? token.value);
const domain = computed<string>(() => String(props.domainId));

function onUserUpdated(newUser: typeof user.value): void {
  canEdit.value = props.canUserEditReport
    || newUser?.hasEntitlement(Entitlements.ReportEntityManagementModule.SaveOrDeleteReports) === true;
}

watch(user, newUser => onUserUpdated(newUser), { immediate: true });

onMounted(async () => {
  try {
    defaultView.value = (await getLSDefaults('reporting'))?.viewFullPath
      ?? (await getLSDefaults('inventory-browser'))?.viewFullPath
      ?? undefined;
  } catch (err) {
    console.log('Failed to find LS default view for reporting', err);
  } finally {
    loaded.value = true;
  }
});

return { __sfc: true,props, user, token, verificationToken, loaded, defaultView, canEdit, apiRoot, jwt, domain, onUserUpdated }
}

})